import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './WidgetStyles.module.css';
import { CopyIcon, ThumbDownIcon, ThumbUpIcon } from './WidgetIcons';

type ReactionBarProps = {
    onCopy?: () => void | Promise<void>;
    durationLabel?: string | null;
    showFeedback?: boolean;
    onThumbUp?: () => void | Promise<void>;
    onThumbDown?: () => void | Promise<void>;
    isSubmittingFeedback?: boolean;
};

type ReactionButton = {
    id: string;
    label: string;
    icon: ReactNode;
    onClick?: () => void | Promise<void>;
    disabled?: boolean;
};

export function ReactionBar({
    onCopy,
    durationLabel,
    showFeedback = false,
    onThumbUp,
    onThumbDown,
    isSubmittingFeedback = false
}: ReactionBarProps) {
    const { t } = useTranslation();
    const buttons: ReactionButton[] = [
        {
            id: 'copy',
            label: t('copyResponse'),
            icon: <CopyIcon />,
            onClick: onCopy,
            disabled: !onCopy
        }
    ];
    if (showFeedback) {
        buttons.push(
            {
                id: 'thumb-up',
                label: t('feedbackThumbsUp'),
                icon: <ThumbUpIcon />,
                onClick: onThumbUp,
                disabled: isSubmittingFeedback || !onThumbUp
            },
            {
                id: 'thumb-down',
                label: t('feedbackThumbsDown'),
                icon: <ThumbDownIcon />,
                onClick: onThumbDown,
                disabled: isSubmittingFeedback || !onThumbDown
            }
        );
    }

    return (
        <div className={styles.reactionBar}>
            <div className={styles.reactionButtonGroup}>
                {buttons.map((button) => (
                    <button
                        key={button.id}
                        className={styles.reactionButton}
                        type="button"
                        aria-label={button.label}
                        disabled={button.disabled}
                        onClick={button.onClick}
                        title={button.label}
                    >
                        {button.icon}
                    </button>
                ))}
            </div>
            {durationLabel ? <span className={styles.reactionMeta}>{durationLabel}</span> : null}
        </div>
    );
}
